import './index.less';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { getUserInfo } from '@/api/api';

const PREFIX = import.meta.env.VITE_PROTOCOL_SCHEME;

type GbaseStep = 'processing' | 'verifying-login' | 'success' | 'error';

function GbaseAccount() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<GbaseStep>('processing');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(20);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    handleGbaseCallback();
  }, [searchParams]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'success' && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (step === 'success' && countdown === 0) {
      // window.close();
      window.location.href = import.meta.env.VITE_FELO_SUBTITLES_URL;
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [step, countdown]);

  const triggerDesktopProtocol = (url: string) => {
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  const handleGbaseCallback = async () => {
    try {
      console.log('GBase callback started');
      console.log('Current URL:', window.location.href);

      // 第一步：处理授权码
      setStep('processing');
      await new Promise((resolve) => setTimeout(resolve, 500));

      // 从URL参数中获取授权码 (支持authCode和code两种参数名)
      const authCode = searchParams.get('authCode') || searchParams.get('code');
      console.log('GBase authCode found:', !!authCode);

      if (!authCode) {
        // 检查是否有错误参数
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          throw new Error(t('gbase.callback.error.auth.failed', {
            message: errorDescription || error,
          }));
        } else {
          throw new Error(t('gbase.callback.error.no.code'));
        }
      }

      // 第二步：验证用户登录状态
      console.log('Verifying user login status...');
      setStep('verifying-login');
      await new Promise((resolve) => setTimeout(resolve, 500));

      const token = Cookies.get(import.meta.env.VITE_COOKIE_SUFFIX as string);
      if (!token) {
        throw new Error(t('gbase.callback.error.not.logged.in'));
      }

      // 验证token有效性
      try {
        const { data } = await getUserInfo();
        if (data.status !== 200) {
          throw new Error('Token validation failed');
        }
        console.log('User login status verified');
      } catch (error) {
        throw new Error(t('gbase.callback.error.token.invalid'));
      }

      // 第三步：回调桌面应用
      console.log('Calling desktop protocol with GBase auth code');
      const callbackUrl = `${PREFIX}://gbase-auth-callback?`
        + `code=${encodeURIComponent(authCode)}&`
        + 'status=SUCCESS&'
        + `timestamp=${Date.now()}`;

      console.log('GBase desktop protocol URL:', callbackUrl);
      triggerDesktopProtocol(callbackUrl);

      setStep('success');
      setMessage(t('gbase.callback.success.desc'));
      setCountdown(20); // 重置倒计时
    } catch (error) {
      console.error('GBase callback error:', error);

      // 错误回调到桌面应用
      const errorMessage = error instanceof Error ? error.message : t('gbase.callback.error.generic');
      const callbackUrl = `${PREFIX}://gbase-auth-callback?`
        + 'status=ERROR&'
        + `message=${encodeURIComponent(errorMessage)}&`
        + `timestamp=${Date.now()}`;

      console.log('GBase desktop protocol error URL:', callbackUrl);
      triggerDesktopProtocol(callbackUrl);

      setStep('error');
      setMessage(errorMessage);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'processing': return t('gbase.callback.processing');
      case 'verifying-login': return t('gbase.callback.verifying');
      case 'success': return t('gbase.callback.success');
      case 'error': return t('gbase.callback.error');
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'processing': return t('gbase.callback.processing.desc');
      case 'verifying-login': return t('gbase.callback.verifying.desc');
      case 'success': return t('gbase.callback.success.close');
      case 'error': return message;
      default: return '';
    }
  };

  return (
    <div className="gbase-gradient-bg min-h-screen flex items-center justify-center">
      {/* Hidden iframe for protocol triggering */}
      <iframe
        ref={iframeRef}
        style={{ display: 'none' }}
        title="GBase Protocol Trigger"
      />

      <div className="max-w-md w-full mx-4">
        {/* Main Card */}
        <div className="gbase-card">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">{t('gbase.callback.title')}</h1>
            <p className="text-gray-500 mt-2 text-sm">{t('gbase.callback.processing.status')}</p>
          </div>

          {/* Status Content */}
          {step !== 'error' && step !== 'success' && (
            <div className="space-y-6">
              <div className="w-12 h-12 mx-auto relative">
                <div className="w-12 h-12 border-3 border-gray-200 rounded-full"></div>
                <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-t-blue-500 rounded-full gbase-spinner"></div>
              </div>

              <div className="space-y-3">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <p className="text-gray-600">{getStepDescription()}</p>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">{getStepTitle()}</h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-medium">{getStepDescription()}</p>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                {t('gbase.callback.returning')} ({countdown}s)
              </div>
            </div>
          )}

          {step === 'error' && (
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-800">{getStepTitle()}</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{getStepDescription()}</p>
                </div>
              </div>

              <div className="gbase-btn-container">
                <button
                  onClick={() => window.location.reload()}
                  className="gbase-btn-primary"
                >
                  <svg className="w-4 h-4 gbase-icon-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{t('gbase.callback.retry')}</span>
                </button>
                <button
                  onClick={() => window.close()}
                  className="gbase-btn-secondary"
                >
                  {t('gbase.callback.close')}
                </button>
              </div>
            </div>
          )}

          {/* Brand Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-center space-x-2 text-gray-400">
              <span className="text-xs">Felo Subtitles</span>
              <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
              <span className="text-xs">GBase</span>
            </div>
            <div className="text-xs text-gray-300 mt-2">{t('gbase.callback.success.status')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GbaseAccount;
